const CSRF_COOKIE = "__Host-healsy-csrf";
const CSRF_HEADER = "X-CSRF-Token";

export class AuthHttpError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "AuthHttpError";
  }
}

export function getCsrfToken(): string | null {
  if (typeof document === "undefined") return null;
  const prefix = `${CSRF_COOKIE}=`;
  const value = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  return value ? decodeURIComponent(value.slice(prefix.length)) : null;
}

async function responseMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { detail?: unknown; message?: unknown };
    if (typeof body.detail === "string") return body.detail;
    if (typeof body.message === "string") return body.message;
  } catch {
    // Odpowiedź bez JSON (np. 502 z reverse proxy).
  }
  return response.status === 503
    ? "Uwierzytelnianie jest chwilowo niedostępne"
    : "Nie udało się wykonać operacji";
}

export async function ensureCsrfToken(): Promise<string> {
  const current = getCsrfToken();
  if (current) return current;
  const response = await fetch("/auth/csrf", {
    credentials: "same-origin",
    cache: "no-store",
  });
  if (!response.ok) {
    throw new AuthHttpError(await responseMessage(response), response.status);
  }
  const body = (await response.json()) as { csrf_token?: string };
  const token = getCsrfToken() || body.csrf_token;
  if (!token) throw new AuthHttpError("Nie udało się ustanowić ochrony CSRF", 503);
  return token;
}

async function authMutation<T>(
  path: string,
  body?: Record<string, unknown>,
  options?: { requireExistingCsrf?: boolean },
): Promise<T> {
  const csrf = options?.requireExistingCsrf
    ? getCsrfToken()
    : await ensureCsrfToken();
  if (!csrf) throw new AuthHttpError("Sesja nie zawiera ochrony CSRF", 401);
  const response = await fetch(path, {
    method: "POST",
    credentials: "same-origin",
    cache: "no-store",
    headers: {
      "Content-Type": "application/json",
      [CSRF_HEADER]: csrf,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) {
    throw new AuthHttpError(await responseMessage(response), response.status);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const browserAuth = {
  register: (data: {
    email: string;
    password: string;
    name?: string;
    referral_code?: string;
  }) => authMutation<{ message: string }>("/auth/register", data),
  activate: (token: string, password: string) =>
    authMutation<void>("/auth/activate", { token, password }),
  login: (email: string, password: string) =>
    authMutation<{ status: string }>("/auth/login", { email, password }),
  googleStatus: async () => {
    const response = await fetch("/auth/google/status", {
      credentials: "same-origin",
      cache: "no-store",
    });
    if (!response.ok) {
      throw new AuthHttpError(await responseMessage(response), response.status);
    }
    return (await response.json()) as { available: boolean };
  },
  googleStart: (returnTarget: "home" | "onboarding" | "security" = "home") =>
    authMutation<{ authorization_url: string }>("/auth/google/start", {
      return_target: returnTarget,
    }),
  googleLinkStart: (currentPassword: string) =>
    authMutation<{ authorization_url: string }>(
      "/auth/google/link/start",
      { current_password: currentPassword },
      { requireExistingCsrf: true },
    ),
  refreshSession: () =>
    authMutation<{ status: string }>("/auth/refresh", undefined, {
      requireExistingCsrf: true,
    }),
  logout: () =>
    authMutation<void>("/auth/logout", undefined, { requireExistingCsrf: true }),
  logoutAll: () =>
    authMutation<void>("/auth/logout-all", undefined, {
      requireExistingCsrf: true,
    }),
  forgotPassword: (email: string) =>
    authMutation<{ message: string }>("/auth/forgot-password", { email }),
  resetPassword: (token: string, password: string) =>
    authMutation<void>("/auth/reset-password", { token, password }),
  changePassword: (currentPassword: string, newPassword: string) =>
    authMutation<void>(
      "/auth/change-password",
      { current_password: currentPassword, new_password: newPassword },
      { requireExistingCsrf: true },
    ),
  // #760: link potwierdzający idzie na nowy adres; adres zmienia się po kliknięciu.
  requestEmailChange: (newEmail: string, currentPassword: string) =>
    authMutation<{ message: string }>(
      "/auth/email/change",
      { new_email: newEmail, current_password: currentPassword },
      { requireExistingCsrf: true },
    ),
  confirmEmailChange: (token: string) =>
    authMutation<void>("/auth/email/confirm", { token }),
};
